import { useState } from 'react';
import { Checkbox } from 'components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ExpandableFilterProps {
  title: string;
  icon?: React.ReactNode;
  options: string[];
  selectedValue: string;
  onSelectionChange: (value: string) => void;
  initialLimit?: number;
  className?: string;
  renderOption?: (option: string) => React.ReactNode;
}

export function ExpandableFilter({
  title,
  icon,
  options,
  selectedValue,
  onSelectionChange,
  initialLimit = 6,
  className = '',
  renderOption,
}: ExpandableFilterProps) {
  const { t } = useTranslation('collab-club');
  const [showMore, setShowMore] = useState(false);

  // Always include the selected option in displayed options
  const getDisplayedOptions = () => {
    if (showMore) return options;

    const visibleOptions = options.slice(0, initialLimit);

    // If selected value is not in the visible options and it's not "all", add it at the beginning
    if (
      selectedValue !== 'all' &&
      !visibleOptions.includes(selectedValue) &&
      options.includes(selectedValue)
    ) {
      // Show selected item first, then other visible options (minus one to maintain limit)
      return [
        ...visibleOptions.filter((opt) => opt !== selectedValue).slice(0, initialLimit - 1),
        selectedValue,
      ];
    }

    return visibleOptions;
  };

  const displayedOptions = getDisplayedOptions();
  const hasMore = options.length > initialLimit;

  return (
    <div className={className}>
      <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
        {icon}
        {title}
      </h3>
      <div className="space-y-3">
        {displayedOptions.map((option) => (
          <div key={option} className="flex items-center space-x-3">
            <Checkbox
              id={`${title.toLowerCase()}-${option}`}
              checked={selectedValue === option}
              onCheckedChange={() => onSelectionChange(option)}
              className="rounded"
            />
            <label
              htmlFor={`${title.toLowerCase()}-${option}`}
              className="text-sm text-gray-700 capitalize cursor-pointer flex-1"
            >
              {renderOption ? renderOption(option) : option}
            </label>
          </div>
        ))}
        {hasMore && (
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-2"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${showMore ? 'rotate-180' : ''}`}
            />
            {showMore ? t('show_less') : t('show_more')}
          </button>
        )}
      </div>
    </div>
  );
}
